import { DatePicker } from '@mui/lab';
import { Divider, Grid, useEventCallback } from '@mui/material';
import { AccordionGeneralityForm } from 'civil-status/view-models/accordion-generality-form';
import { Fragment, memo } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { FormikErrors, FormikTouched } from 'formik';
import ListAnsc from '../../../../../../../shared/list-ansc';
import { ItemVM } from 'shell/view-models/item';
import NationalityList, { NationalityVM } from 'shell/components/shared/nationality-list';

interface FormGeneralityBaseProps {
  disabled?: boolean;
  generality?: AccordionGeneralityForm;
  errorsGenerality?: FormikErrors<AccordionGeneralityForm>;
  touchedGenerality?: FormikTouched<AccordionGeneralityForm>;
  onChangeGenerality: (
    key: keyof AccordionGeneralityForm,
    value: AccordionGeneralityForm[keyof AccordionGeneralityForm]
  ) => void;
  requiredOnlyNameSurname?: boolean;
  nothingRequired?: boolean;
  isIntCit2?: boolean;
  isIntCit3?: boolean;
}

const FormGeneralityBaseComponent = ({
  disabled = false,
  generality,
  errorsGenerality,
  touchedGenerality,
  onChangeGenerality,
  requiredOnlyNameSurname = false,
  nothingRequired = false,
  isIntCit2 = false,
  isIntCit3 = false,
}: FormGeneralityBaseProps): React.ReactElement => {
  const handleListTypologyDocument = useEventCallback((item?: ItemVM) => {
    onChangeGenerality('documentTypology', item?.id);
  });

  const handleListReleaseFrom = useEventCallback((item?: ItemVM) => {
    onChangeGenerality('documentReleaseFrom', item?.id);
  });

  const handleOtherCitizenshipListChange = useEventCallback(
    (state: NationalityVM | null | undefined) => {
      onChangeGenerality('otherCitizenship', state);
    }
  );

  return (
    <Grid container spacing={2} item>
      {isIntCit2 ? undefined : (
        <Fragment>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              label={`Numero documento ${requiredOnlyNameSurname || nothingRequired ? '' : '*'}`}
              aria-label="Numero documento"
              id="documentNumber"
              name="documentNumber"
              fullWidth
              disabled={disabled}
              helperText={errorsGenerality?.documentNumber}
              error={touchedGenerality?.documentNumber && Boolean(errorsGenerality?.documentNumber)}
              type="text"
              variant="outlined"
              value={generality?.documentNumber || ''}
              onChange={(event) => {
                onChangeGenerality('documentNumber', event.target.value);
              }}
              inputProps={{
                maxLength: '240',
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ListAnsc
              label="Tipologia documento"
              idDominio="25"
              required={!(requiredOnlyNameSurname || nothingRequired)}
              value={generality?.documentTypology || ''}
              onChange={handleListTypologyDocument}
              disabled={disabled}
              error={
                Boolean(errorsGenerality?.documentTypology) && touchedGenerality?.documentTypology
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DatePicker
              label={`Data scadenza ${requiredOnlyNameSurname || nothingRequired ? '' : '*'}`}
              aria-label="Data scadenza"
              value={generality?.documentExpiryDate || null}
              onChange={(date) => {
                onChangeGenerality('documentExpiryDate', date);
              }}
              disabled={disabled}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="documentExpiryDate"
                  fullWidth
                  helperText={errorsGenerality?.documentExpiryDate}
                  error={
                    touchedGenerality?.documentExpiryDate &&
                    Boolean(errorsGenerality?.documentExpiryDate)
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ListAnsc
              label="Rilasciato da"
              idDominio="20"
              required={!(requiredOnlyNameSurname || nothingRequired)}
              value={generality?.documentReleaseFrom || ''}
              onChange={handleListReleaseFrom}
              disabled={disabled}
              error={
                Boolean(errorsGenerality?.documentReleaseFrom) &&
                touchedGenerality?.documentReleaseFrom
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DatePicker
              label={`Data rilascio ${requiredOnlyNameSurname || nothingRequired ? '' : '*'}`}
              aria-label="Data rilascio"
              value={generality?.documentReleaseDate || null}
              onChange={(date) => {
                onChangeGenerality('documentReleaseDate', date);
              }}
              disabled={disabled}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="documentReleaseDate"
                  fullWidth
                  helperText={errorsGenerality?.documentReleaseDate}
                  error={
                    touchedGenerality?.documentReleaseDate &&
                    Boolean(errorsGenerality?.documentReleaseDate)
                  }
                />
              )}
            />
          </Grid>
        </Fragment>
      )}
      {/* {isIntCit3 ? undefined : (
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                id="isApolide"
                checked={generality?.isApolide || false}
                onChange={() => {
                  onChangeGenerality('isApolide', !generality?.isApolide);
                }}
                inputProps={{ 'aria-label': 'Apolide' }}
                disabled={disabled}
              />
            }
            label="Apolide"
          />
        </Grid>
      )} */}
      {isIntCit3 ? (
        <Grid item xs={12} md={4}>
          <NationalityList
            label="Ulteriore cittadinanza di cui si è in possesso"
            value={generality?.otherCitizenship || null}
            onChange={handleOtherCitizenshipListChange}
            required={!(requiredOnlyNameSurname || nothingRequired)}
            disabled={disabled}
            id="otherCitizenship"
            error={
              (touchedGenerality?.otherCitizenship as unknown as FormikTouched<NationalityVM>) &&
              Boolean(errorsGenerality?.otherCitizenship as FormikErrors<NationalityVM>)
            }
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

FormGeneralityBaseComponent.displayName = 'Form generality BASE - Base IntCit component';

const BaseIntCit = memo(FormGeneralityBaseComponent);

export default BaseIntCit;
